/* Iter51 E29c — Admin Cleanup Test Data UI.
 *
 * Buton + dialog cu Preview (dry-run) → Execute. Folosește endpoint-ul
 * POST /api/admin/cleanup-test-data (?execute=true|false).
 */
import { useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Trash2, AlertTriangle, RefreshCw, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const AdminCleanup = () => {
  const [previewing, setPreviewing] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [preview, setPreview] = useState(null);     // { import_batches, sales, contracts, ... }
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const runPreview = async () => {
    setPreviewing(true);
    setLastResult(null);
    try {
      const r = await axios.post(`${API_URL}/admin/cleanup-test-data`);
      setPreview(r.data.preview);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare la preview cleanup");
    } finally {
      setPreviewing(false);
    }
  };

  const runExecute = async () => {
    setExecuting(true);
    setConfirmOpen(false);
    try {
      const r = await axios.post(`${API_URL}/admin/cleanup-test-data?execute=true`);
      setLastResult(r.data.deleted);
      setPreview(null);
      toast.success("Cleanup complet — datele test au fost șterse.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare la execute cleanup");
    } finally {
      setExecuting(false);
    }
  };

  const total = (p) => (p ? p.import_batches + p.sales + p.contracts + p.orphan_clients + p.offers : 0);
  const isEmpty = preview && total(preview) === 0;

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Curăță date test</h1>
        <p className="text-sm text-slate-500 mt-1">
          Șterge datele create de pytest-uri și suite de testare automată — fără să atingă datele reale SF.
        </p>
      </div>

      <Card className="border-amber-200 bg-amber-50/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            Ce se șterge?
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-amber-900 space-y-1">
          <p>• <strong>Contracte</strong> cu POD prefix: <code>POD-E2*</code>, <code>POD-CANCEL-*</code>, <code>POD-STRESS-*</code>, <code>POD-BADGE-*</code>, etc.</p>
          <p>• <strong>Vânzări</strong> linked la aceste contracte sau cu Dossier Code <code>DOS-E2*</code> / <code>D-E2*</code>.</p>
          <p>• <strong>Clienți</strong> orfani (doar cei fără alte contracte rămase).</p>
          <p>• <strong>Oferte</strong> cu cod <code>sf-e28-*</code> (auto-create de pytest).</p>
          <p>• <strong>ImportBatches</strong> cu filename test cunoscut (e25.xlsx, dryrun.xlsx, stress-*, badge-*, etc.).</p>
          <p className="pt-2 font-semibold">Nu se șterg PODs reali (CEZ-*, EON-*, etc.) sau orice batch cu nume diferit.</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Pas 1 — Preview (dry-run)</CardTitle>
          <CardDescription>
            Vezi cât din DB ar fi șters, fără să modifici nimic încă.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            onClick={runPreview}
            disabled={previewing || executing}
            data-testid="cleanup-preview-btn"
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {previewing ? (
              <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Se calculează...</>
            ) : (
              <><RefreshCw className="h-4 w-4 mr-2" /> Rulează Preview</>
            )}
          </Button>

          {preview && (
            <div className="mt-4 rounded-lg border border-slate-200 overflow-hidden">
              <table className="w-full text-sm">
                <tbody>
                  {[
                    ["ImportBatches", preview.import_batches],
                    ["Vânzări (Sales)", preview.sales],
                    ["Contracte", preview.contracts],
                    ["Clienți orfani", preview.orphan_clients],
                    ["Oferte test", preview.offers],
                  ].map(([label, n]) => (
                    <tr key={label} className="border-b border-slate-100 last:border-0">
                      <td className="px-4 py-2 text-slate-600">{label}</td>
                      <td className="px-4 py-2 text-right font-mono font-semibold" data-testid={`preview-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                        {n.toLocaleString("ro-RO")}
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-slate-50">
                    <td className="px-4 py-2 font-semibold text-slate-800">Total</td>
                    <td className="px-4 py-2 text-right font-mono font-bold" data-testid="preview-total">
                      {total(preview).toLocaleString("ro-RO")}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {preview && !isEmpty && (
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="text-base text-red-700">Pas 2 — Execută ștergerea</CardTitle>
            <CardDescription>
              Ireversibil. Datele de test vor fi șterse din DB într-o singură tranzacție.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => setConfirmOpen(true)}
              disabled={executing}
              data-testid="cleanup-execute-btn"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {executing ? "Se șterge..." : `Șterge ${total(preview).toLocaleString("ro-RO")} înregistrări`}
            </Button>
          </CardContent>
        </Card>
      )}

      {preview && isEmpty && (
        <Card className="border-emerald-200 bg-emerald-50/40">
          <CardContent className="pt-6 flex items-center gap-3 text-emerald-900">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            <p>Nu există date de test în DB. Nimic de șters.</p>
          </CardContent>
        </Card>
      )}

      {lastResult && (
        <Card className="border-emerald-200 bg-emerald-50/40">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base text-emerald-800">
              <CheckCircle2 className="h-5 w-5" />
              Cleanup finalizat
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-emerald-900">
            Șterse: {lastResult.import_batches} batches • {lastResult.sales} sales •{" "}
            {lastResult.contracts} contracts • {lastResult.orphan_clients} clienți • {lastResult.offers} oferte.
          </CardContent>
        </Card>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Confirmă ștergerea
            </AlertDialogTitle>
            <AlertDialogDescription>
              Vei șterge <strong>{preview ? total(preview).toLocaleString("ro-RO") : 0}</strong> înregistrări
              din baza de date. Această acțiune este <strong>ireversibilă</strong>.
              <br /><br />
              Datele tale reale (PODs SF reale, agenți, oferte cu cod diferit de <code>sf-e28-*</code>) nu sunt afectate.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cleanup-cancel-btn">Anulează</AlertDialogCancel>
            <AlertDialogAction
              onClick={runExecute}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="cleanup-confirm-btn"
            >
              Da, șterge definitiv
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Iter51 E29e — Wipe ALL Sales (nuclear option) */}
      <WipeAllSalesSection />
    </div>
  );
};


// ===== WIPE ALL SECTION (separate component) =====
const WipeAllSalesSection = () => {
  const [previewing, setPreviewing] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const runPreview = async () => {
    setPreviewing(true);
    setLastResult(null);
    try {
      const r = await axios.post(`${API_URL}/admin/wipe-all-sales`);
      setPreview(r.data.preview);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare la preview");
    } finally {
      setPreviewing(false);
    }
  };

  const runExecute = async () => {
    if (confirmText !== "STERGE-TOATE-VANZARILE") {
      toast.error("Text de confirmare incorect. Tastează exact: STERGE-TOATE-VANZARILE");
      return;
    }
    setExecuting(true);
    setConfirmOpen(false);
    try {
      const r = await axios.post(
        `${API_URL}/admin/wipe-all-sales?execute=true&confirm_token=STERGE-TOATE-VANZARILE`
      );
      setLastResult(r.data.deleted);
      setPreview(null);
      setConfirmText("");
      toast.success("Wipe complet. Toate vânzările au fost șterse.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare la wipe");
    } finally {
      setExecuting(false);
    }
  };

  const total = (p) => p ? p.sales + p.contracts + p.clients + p.import_batches + p.processing_cases + p.contract_statuses : 0;

  return (
    <Card className="border-red-300 bg-red-50/30 mt-8">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-red-700 text-base">
          <AlertTriangle className="h-5 w-5" />
          ☢ Wipe complet — Șterge TOATE vânzările, contractele, clienții
        </CardTitle>
        <CardDescription className="text-red-800">
          Operațiune <strong>distructivă</strong> pentru reset complet al datelor business înainte de
          import-ul real cu fișierele finale. Datele despre <strong>Useri, Agenți și Oferte (catalog)</strong>
          {" "}sunt păstrate.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Button
          onClick={runPreview}
          disabled={previewing || executing}
          variant="outline"
          className="border-red-300 text-red-700 hover:bg-red-100"
          data-testid="wipe-preview-btn"
        >
          {previewing ? (
            <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Se calculează...</>
          ) : (
            <><RefreshCw className="h-4 w-4 mr-2" /> Vezi câte vor fi șterse</>
          )}
        </Button>

        {preview && (
          <div className="rounded-lg border border-red-300 bg-white overflow-hidden">
            <table className="w-full text-sm">
              <tbody>
                {[
                  ["Vânzări (Sales)", preview.sales],
                  ["Contracte", preview.contracts],
                  ["Clienți", preview.clients],
                  ["ImportBatches", preview.import_batches],
                  ["ProcessingCase", preview.processing_cases],
                  ["ContractStatus", preview.contract_statuses],
                ].map(([label, n]) => (
                  <tr key={label} className="border-b border-red-100 last:border-0">
                    <td className="px-4 py-2 text-slate-600">{label}</td>
                    <td className="px-4 py-2 text-right font-mono font-semibold" data-testid={`wipe-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                      {n.toLocaleString("ro-RO")}
                    </td>
                  </tr>
                ))}
                <tr className="bg-red-100">
                  <td className="px-4 py-2 font-bold text-red-800">Total</td>
                  <td className="px-4 py-2 text-right font-mono font-bold text-red-800" data-testid="wipe-total">
                    {total(preview).toLocaleString("ro-RO")}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {preview && total(preview) > 0 && (
          <Button
            onClick={() => setConfirmOpen(true)}
            disabled={executing}
            className="bg-red-700 hover:bg-red-800 text-white"
            data-testid="wipe-execute-btn"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Continuă către confirmare
          </Button>
        )}

        {lastResult && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
            <CheckCircle2 className="h-4 w-4 inline mr-1" />
            Șterse: {lastResult.sales} sales • {lastResult.contracts} contracts •{" "}
            {lastResult.clients} clienți • {lastResult.import_batches} batches.
          </div>
        )}
      </CardContent>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" />
              CONFIRMARE FINALĂ — ireversibil
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <p>
                  Vei șterge <strong>{preview ? total(preview).toLocaleString("ro-RO") : 0}</strong> înregistrări:
                  <br />• {preview?.sales || 0} vânzări
                  <br />• {preview?.contracts || 0} contracte
                  <br />• {preview?.clients || 0} clienți
                  <br />• {preview?.import_batches || 0} batches istoric
                </p>
                <p className="text-red-700 font-semibold">
                  Datele <strong>NU pot fi recuperate</strong> (decât din backup Supabase).
                </p>
                <div>
                  <p className="text-xs mb-1 text-slate-600">
                    Pentru a confirma, tastează exact: <code className="bg-slate-100 px-1 rounded">STERGE-TOATE-VANZARILE</code>
                  </p>
                  <input
                    type="text"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    className="w-full px-3 py-2 border border-red-300 rounded-md font-mono text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                    placeholder="STERGE-TOATE-VANZARILE"
                    data-testid="wipe-confirm-text-input"
                    autoFocus
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="wipe-cancel-btn">Anulează</AlertDialogCancel>
            <AlertDialogAction
              onClick={runExecute}
              disabled={confirmText !== "STERGE-TOATE-VANZARILE"}
              className="bg-red-700 hover:bg-red-800 text-white disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="wipe-final-confirm-btn"
            >
              ȘTERGE TOTUL
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default AdminCleanup;
